import { inject, Injectable } from '@angular/core';
import { ApiV3ServiceGetCouponDiscountGet$Params } from '@api-gen/fn/coupon/api-v-3-service-get-coupon-discount-get';
import { ApiV3ServiceGetTrmOrgGet$Params } from '@api-gen/fn/service-v-2-v/api-v-3-service-get-trm-org-get';
import { ApiV3ServiceListTrmOrgGet$Params } from '@api-gen/fn/service-v-2-v/api-v-3-service-list-trm-org-get';
import { ApiV3ServiceListPurchaseSourceGet$Params } from '@api-gen/fn/ticket/api-v-3-service-list-purchase-source-get';
import { CouponDiscountApiFrom } from '@api-gen/models/coupon-discount-api-from';
import { GetTrmOrgResponseApiFrom } from '@api-gen/models/get-trm-org-response-api-from';
import { ListTrmOrgResponseApiFrom } from '@api-gen/models/list-trm-org-response-api-from';
import { PurchaseSourceApiFrom } from '@api-gen/models/purchase-source-api-from';
import { SetTicketBasketCancelRequestApiFrom } from '@api-gen/models/set-ticket-basket-cancel-request-api-from';
import { ApiFromCouponService } from '@api-gen/services/api-from-coupon.service';
import { ApiFromServiceV2VService } from '@api-gen/services/api-from-service-v-2-v.service';
import { ApiFromTicketService } from '@api-gen/services/api-from-ticket.service';
import { ApiFromTicketsService } from '@api-gen/services/api-from-tickets.service';
import { ErrorResponse } from '@api/models';
import { BasketItemModel, BasketModelWithLocalFields, BasketPaymentStatus } from '@core/models/basket-item.model';
import { startWithErrors } from '@core/operators/start-with-errors';
import { startWithLoading } from '@core/operators/start-with-loading';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { CustomRxState } from '@core/state/rx.state';
import { LocalStorage } from '@core/storage/local-storage';
import { StorageKeys } from '@core/storage/storage.keys';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { findIndex } from 'lodash-es';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';


// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare function uuidv4(): any;

export interface IBasketState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    basket: BasketModelWithLocalFields;
    defaultBasketTransportManager: ListTrmOrgResponseApiFrom;
    defaultBasketTransportManagerData: GetTrmOrgResponseApiFrom;
    transportManagers: ListTrmOrgResponseApiFrom[];
    paymentMethods: PurchaseSourceApiFrom[];
}

export interface BasketAction {
    setBasket: BasketModelWithLocalFields;
    setDefaultBasketTransportManager: ListTrmOrgResponseApiFrom;
    requestTransportManagers: ListTrmOrgResponseApiFrom[];
    requestTransportManagerData: GetTrmOrgResponseApiFrom;
    requestPaymentMethods: PurchaseSourceApiFrom[];
}

@Injectable({
    providedIn: 'root',
})
export class BasketState extends CustomRxState<IBasketState> {
    @LocalStorage(StorageKeys.Basket)
    public storageBasket: string;

    @LocalStorage(StorageKeys.BasketTrmOrg)
    public storageBasketTrmOrg: string;

    readonly #apiFromServiceV2VService: ApiFromServiceV2VService = inject(ApiFromServiceV2VService);
    readonly #apiFromCouponService: ApiFromCouponService = inject(ApiFromCouponService);
    readonly #apiFromTicketService: ApiFromTicketService = inject(ApiFromTicketService);
    readonly #apiFromTicketsService: ApiFromTicketsService = inject(ApiFromTicketsService);
    readonly #actions: RxActions<BasketAction> = new RxActionFactory<BasketAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly errors$: Observable<Record<StartWithUnit, ErrorResponse>> = this.select('errors');
    public readonly basket$: Observable<BasketModelWithLocalFields> = this.select('basket');
    public readonly transportManagers$: Observable<ListTrmOrgResponseApiFrom[]> = this.select('transportManagers');
    public readonly paymentMethods$: Observable<PurchaseSourceApiFrom[]> = this.select('paymentMethods');
    public readonly defaultBasketTransportManager$: Observable<ListTrmOrgResponseApiFrom> = this.select('defaultBasketTransportManager');
    public readonly defaultBasketTransportManagerData$: Observable<GetTrmOrgResponseApiFrom> =
        this.select('defaultBasketTransportManagerData');


    constructor() {
        super();

        this.connectSelectors();
        this.setDefaultState();
    }

    public get defaultBasketTransportManager(): ListTrmOrgResponseApiFrom {
        return this.get('defaultBasketTransportManager');
    }

    public requestTransportManagers(body?: ApiV3ServiceListTrmOrgGet$Params): void {
        this.hold(
            this.#apiFromServiceV2VService.apiV3ServiceListTrmOrgGet(body),
            this.#actions.requestTransportManagers,
        );
    }

    public requestTransportManagerData(body?: ApiV3ServiceGetTrmOrgGet$Params): void {
        this.hold(
            this.#apiFromServiceV2VService.apiV3ServiceGetTrmOrgGet(body),
            this.#actions.requestTransportManagerData,
        );
    }

    public requestPaymentMethods(body?: ApiV3ServiceListPurchaseSourceGet$Params): void {
        this.hold(
            this.#apiFromTicketService.apiV3ServiceListPurchaseSourceGet(body),
            this.#actions.requestPaymentMethods,
        );
    }

    public applyBasketCoupon(body: ApiV3ServiceGetCouponDiscountGet$Params): void {
        this.hold(
            this.#apiFromCouponService.apiV3ServiceGetCouponDiscountGet(body).pipe(
                startWithLoading(this, StartWithUnit.ApplyBasketCoupon),
                startWithErrors(this, StartWithUnit.ApplyBasketCoupon),
            ),
            (response: CouponDiscountApiFrom) => {
                this.applyBasketDiscount(response.amount, body.cpnNo);
            },
        );
    }

    public setDefaultBasketTransportManager(body: ListTrmOrgResponseApiFrom): void {
        this.#actions.setDefaultBasketTransportManager(body);
    }

    public purchaseBasket(body: BasketModelWithLocalFields): Observable<string[]> {
        return ((body.status === BasketPaymentStatus.Locked) ?
            this.cancelBasket({ extId: body.basketId }).pipe(
                switchMap(() => this.#apiFromTicketsService.apiV3ServiceAddBasketPost(
                    {
                        body: {
                            ...body,
                            basketId: this.getAndUpdateBasketId(),
                        },
                    },
                ).pipe(
                    startWithLoading(this, StartWithUnit.PurchaseBasket),
                    startWithErrors(this, StartWithUnit.PurchaseBasket),
                )),
            ) : this.#apiFromTicketsService.apiV3ServiceAddBasketPost({ body }).pipe(
                startWithLoading(this, StartWithUnit.PurchaseBasket),
                startWithErrors(this, StartWithUnit.PurchaseBasket),
            ));
    }

    public cancelBasket(body: SetTicketBasketCancelRequestApiFrom): Observable<void> {
        return this.#apiFromTicketsService.apiV3ServiceSetTicketBasketCancelPost({
            body,
        }).pipe(
            catchError(() => {
                this.completeBasket();

                return EMPTY;
            }),
        );
    }

    public addToBasket(basketItem: BasketItemModel): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        const exist: number = this.findExistingBasketItemIndex(basketItem);

        if (exist > -1) {
            basket.basketItems[exist].cnt++;
        } else {
            basket.basketItems.push({
                ...basketItem,
                cnt: 1,
            });
        }

        this.update(basket);
        this.updateWithDiscountLogic(basket);
    }

    /**
     * Delete item from basket
     * @param basketItem - Basket item id
     */
    public removeFromBasket(basketItem: BasketItemModel): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        const exist: number = this.findExistingBasketItemIndex(basketItem);

        if (exist > -1) {
            basket.basketItems.splice(exist, 1);
        }

        this.update(basket);
        this.updateWithDiscountLogic(basket);
    }

    /**
     * Increase amount item in basket
     * @param basketItem - basket item
     * @param amount - amount increase
     */
    public increaseCnt(basketItem: BasketItemModel, amount?: number): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        const exist: number = this.findExistingBasketItemIndex(basketItem);

        if (!amount) {
            amount = 1;
        }

        if (exist > -1) {
            basket.basketItems[exist].cnt += amount;
        }

        this.update(basket);
        this.updateWithDiscountLogic(basket);
    }

    /**
     * Decrease amount item in basket
     * @param basketItem - basket item
     * @param amount - amount decrease
     */
    public decreaseCnt(basketItem: BasketItemModel, amount?: number): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        const exist: number = this.findExistingBasketItemIndex(basketItem);

        if (!amount) {
            amount = 1;
        }

        basket.basketItems[exist].cnt -= amount;

        if (basket.basketItems[exist].cnt <= 0) {
            this.removeFromBasket(basket.basketItems[exist]);
        } else {
            this.update(basket);
            this.updateWithDiscountLogic(basket);
        }
    }

    /**
     * Apply discount, return modify basket
     * @param amount - Amount discount
     * @param cpnNo - Coupon number
     */
    public applyBasketDiscount(amount: number, cpnNo: string): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        basket.discAmount = amount;
        basket.cpnNo = cpnNo;
        this.update(basket);
    }

    /**
     * Clear basket
     */
    public clearBasket(): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        basket.basketItems = [];
        this.requestTransportManagers();
        this.update(basket);
    }

    /**
     * Complete basket when status payment is success
     */
    public completeBasket(): void {
        this.update(this.initDefaultBasket());
    }

    /**
     * Lock basket for re-payment
     */
    public lockBasket(): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        basket.status = BasketPaymentStatus.Locked;
        this.update(basket);
    }

    /**
     * Reset basket discount
     */
    public resetBasketDiscount(): void {
        const basket: BasketModelWithLocalFields = this.get('basket');
        basket.total += basket.discAmount;
        basket.discAmount = null;
        basket.cpnNo = null;

        this.update(basket);
    }

    /**
     * Update basket Id and return new value
     */
    public getAndUpdateBasketId(): string {
        const basket: BasketModelWithLocalFields = this.get('basket');
        const newBasketId: string = uuidv4();

        basket.basketId = newBasketId;
        this.update(basket);

        return newBasketId;
    }

    /**
     * Get total price basket
     * @param basketItems
     */
    public static getTotalPrice(basketItems: BasketItemModel[]): number {
        // eslint-disable-next-line unicorn/no-array-reduce
        return basketItems.reduce(
            (accumulator: number, currentValue: BasketItemModel): number => accumulator + currentValue.price * currentValue.cnt, 0,
        );
    }

    /**
     * Is Empty basket flag
     */
    public get isEmpty(): boolean {
        return this.get('basket')?.basketItems?.length === 0;
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            basket: this.storageBasket ? JSON.parse(this.storageBasket) : this.initDefaultBasket(),
            defaultBasketTransportManagerData: null,
            defaultBasketTransportManager: this.storageBasketTrmOrg ? JSON.parse(this.storageBasketTrmOrg) : null,
        });

        /** For update force defaultBasketTransportManager */
        if (this.get('defaultBasketTransportManager')) {
            this.#actions.setDefaultBasketTransportManager(this.get('defaultBasketTransportManager'));
        }
    }

    private connectSelectors(): void {
        this.connect('transportManagers', this.#actions.requestTransportManagers$.pipe(
            filter(Boolean),
            tap((transportManagers: ListTrmOrgResponseApiFrom[]) => {
                if (!this.get('defaultBasketTransportManager') && transportManagers.length > 0) {
                    this.#actions.setDefaultBasketTransportManager(transportManagers[0]);
                }
            })),
        );

        this.connect('defaultBasketTransportManagerData', this.#actions.requestTransportManagerData$);
        this.connect('paymentMethods', this.#actions.requestPaymentMethods$);

        this.connect('defaultBasketTransportManager', this.#actions.setDefaultBasketTransportManager$.pipe(
            filter(Boolean),
            tap((transportManager: ListTrmOrgResponseApiFrom) => {
                this.storageBasketTrmOrg = JSON.stringify(transportManager);

                if (!this.isEmpty) {
                    this.requestTransportManagers({ selTrmId: transportManager.trmId });
                }

                this.requestTransportManagerData({ trmId: transportManager.trmId });
            }),
        ));

        this.connect('basket', this.#actions.setBasket$.pipe(
            filter(Boolean),
            tap((basket: BasketModelWithLocalFields) => {
                if (this.firstUpdate()) {
                    this.requestTransportManagers({ selTrmId: this.get('defaultBasketTransportManager').trmId });
                }

                if (basket?.basketItems?.length === 0) {
                    basket.cpnNo = null;
                    basket.discAmount = null;
                }

                this.storageBasket = JSON.stringify(basket);
            }),
        ));
    }

    private initDefaultBasket(): BasketModelWithLocalFields {
        return {
            basketId: uuidv4(),
            basketItems: [],
            total: null,
            totalSource: null,
            status: BasketPaymentStatus.NotPaid,
            srcId1: null,
            payId1: null,
            amount1: null,
            srcId2: null,
            payId2: null,
            amount2: null,
            discAmount: null,
            cpnNo: null,
        };
    }

    /**
     * Update basket value
     * @private
     */
    private update(basket: BasketModelWithLocalFields): void {
        if (!basket.basketItems) {
            basket.discAmount = null;
            basket.cpnNo = null;
        }

        basket.total =
            BasketState.getTotalPrice(basket.basketItems) - (basket.discAmount || 0);

        basket.totalSource = BasketState.getTotalPrice(basket.basketItems);

        if (basket.total < 0) {
            basket.total = 0;
        }

        /** TODO check work total format */
        // this.basketLocal.totalFormat = ''this.currencyFormatPipe.transform(this.basketLocal.total)'';

        this.#actions.setBasket({ ...basket });
    }

    /**
     * Update basket value with cpnNumber
     * @private
     */
    private updateWithDiscountLogic(basket: BasketModelWithLocalFields): void {
        if (basket.cpnNo) {
            this.hold(
                this.#apiFromCouponService.apiV3ServiceGetCouponDiscountGet({
                    cpnNo: basket.cpnNo,
                    basketExtId: basket.basketId,
                    sum: +basket.totalSource,
                }).pipe(
                    startWithLoading(this, StartWithUnit.ApplyBasketCoupon),
                    startWithErrors(this, StartWithUnit.ApplyBasketCoupon),
                ),
                (response: CouponDiscountApiFrom) => {
                    this.applyBasketDiscount(response.amount, basket.cpnNo);
                },
            );
        } else {
            this.update(basket);
        }
    }

    /**
     * Find item in basket
     * @private
     */
    private findExistingBasketItemIndex(basketItem: BasketItemModel): number {
        const basket: BasketModelWithLocalFields = this.get('basket');

        return findIndex(basket?.basketItems, (item: BasketItemModel) =>
            item.trfId === basketItem.trfId &&
            basketItem?.fromDate === item.fromDate &&
            basketItem?.stopTo === item.stopTo &&
            basketItem?.stopFrom === item.stopFrom,
        );
    }

    /**
     * First update basket flag
     */
    private firstUpdate(): boolean {
        const basket: BasketModelWithLocalFields = this.get('basket');

        return basket?.basketItems?.length === 1 && basket?.basketItems[0]?.cnt === 1;
    }
}
